import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { SITE_URL } from '@goodfellas/environment'

import {
  IOS_DOWNLOAD_URL,
  PLAY_STORE_URL,
  androidIntentUrl,
} from '../../constants/appLinks'
import { useAuth } from '../../contexts/AuthContext'
import s from '../landing/landing.module.css'
import { usePlatform } from './platform'

/**
 * Gate de app estilo "Instagram antigo": o conteúdo é renderizado e indexável
 * (o overlay só aparece após gesto do usuário — Googlebot não rola a página,
 * então nunca o vê e não conta como interstitial intrusivo), mas quem navega
 * no celular é convidado a continuar no app e não segue interagindo sem ele.
 *
 * - Android: "Abrir no app" via intent:// (abre o app ou cai na Play Store).
 * - iOS: TestFlight (destino oficial enquanto não há listagem na App Store).
 * - Desktop: modal dispensável com os links das lojas.
 * - Usuários logados na web (fluxos da Avaliação 360) não são bloqueados.
 */
export function AppGateOverlay({ enabled = true }: { enabled?: boolean }) {
  const { t } = useTranslation('gates')
  const router = useRouter()
  const { token } = useAuth()
  const platform = usePlatform()
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const active = enabled && !token && !dismissed

  useEffect(() => {
    if (!active || visible) return undefined

    const threshold = () => window.innerHeight * 0.6
    const onScroll = () => {
      if (window.scrollY > threshold()) setVisible(true)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [active, visible])

  // No mobile o gate é firme: trava o scroll do conteúdo atrás do overlay.
  useEffect(() => {
    const lock = visible && active && platform !== 'desktop'
    document.body.style.overflow = lock ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [visible, active, platform])

  if (!active || !visible) return null

  const absoluteUrl = SITE_URL.replace(/\/+$/, '') + router.asPath.split('?')[0]

  const primary =
    platform === 'android'
      ? { href: androidIntentUrl(absoluteUrl), label: t('common.openInApp') }
      : platform === 'ios'
      ? { href: IOS_DOWNLOAD_URL, label: t('common.downloadApp') }
      : { href: PLAY_STORE_URL, label: t('common.downloadApp') }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('common.dialogAria')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: platform === 'desktop' ? 'center' : 'flex-end',
        justifyContent: 'center',
        background: 'rgba(16,16,19,.72)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className={s.page}
        style={{
          background: '#fff',
          borderRadius: platform === 'desktop' ? 24 : '24px 24px 0 0',
          padding: '34px 30px 40px',
          width: platform === 'desktop' ? 'min(440px, 92vw)' : '100%',
          maxWidth: 520,
          textAlign: 'center',
          position: 'relative',
          boxShadow: '0 -24px 60px rgba(0,0,0,.35)',
        }}
      >
        {platform === 'desktop' && (
          <button
            onClick={() => setDismissed(true)}
            aria-label={t('common.continueBrowser')}
            style={{
              position: 'absolute',
              top: 16,
              right: 20,
              border: 0,
              background: 'transparent',
              fontSize: 22,
              color: '#8b8b8b',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}

        <div className={s.wordmark} style={{ color: '#EC1B2E' }}>
          Goodfellas
        </div>
        <h2
          className={s.d}
          style={{ fontSize: 'clamp(34px,7vw,44px)', margin: '14px 0 0' }}
        >
          {t('common.overlayTitle1')}
          <br />
          <span className={s.outR}>{t('common.overlayTitle2')}</span>
        </h2>
        <p
          className={s.lead}
          style={{ fontSize: 16, margin: '14px auto 0', maxWidth: 360 }}
        >
          {t('common.overlayLead')}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 26,
          }}
        >
          <a
            className={`${s.btn} ${s.btnR}`}
            href={primary.href}
            {...(primary.href.startsWith('https://')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {primary.label}
          </a>
          {platform === 'desktop' && (
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <a
                className={s.btn}
                href={IOS_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.appStore')}
              </a>
              <a
                className={s.btn}
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.googlePlay')}
              </a>
            </div>
          )}
        </div>

        <div
          className={s.cond}
          style={{
            marginTop: 18,
            fontSize: 13,
            letterSpacing: '.14em',
            color: '#8b8b8b',
          }}
        >
          {t('common.free')}
        </div>
      </div>
    </div>
  )
}
